import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { LANDING_CONTENT, type Testimonial } from "../content";
import { useReveal } from "../useReveal";
import { PhotoBrief } from "./PhotoSlot";
import { PlaceholderBadge } from "./PlaceholderBadge";
import { REVEAL_TRANSITION, Reveal, revealState } from "./Reveal";
import { Section, SectionHeading } from "./Section";

// A monogram avatar: the customer's initial in a soft coral disc. The honest MVP
// stand-in for a real pet photo — tasteful, clearly not a stock headshot, and
// swapped for the customer's own pet image (Testimonial.avatar) when we have one.
function Monogram({ name }: { name: string }) {
  const initial = name.trim().charAt(0).toUpperCase();
  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 font-display text-sm font-semibold text-primary"
    >
      {initial}
    </span>
  );
}

function VoiceCard({ voice, index }: { voice: Testimonial; index: number }) {
  const { t } = useTranslation("landing");
  const { ref, revealed } = useReveal<HTMLLIElement>();
  const name = t(`promise.voices.${voice.id}.name`);
  return (
    <li
      ref={ref}
      data-revealed={revealed}
      style={{ transitionDelay: `${index * 80}ms` }}
      className={cn(
        REVEAL_TRANSITION,
        revealState(revealed),
        "relative flex flex-col rounded-xl border border-border bg-paper p-6 shadow-sm",
      )}
    >
      {voice.placeholder ? <PlaceholderBadge /> : null}
      <blockquote className="flex-1 text-pretty leading-relaxed text-foreground/90">
        {t(`promise.voices.${voice.id}.quote`)}
      </blockquote>
      <figcaption className="mt-5 flex items-center gap-3">
        <Monogram name={name} />
        <span className="text-sm leading-tight">
          <span className="font-medium text-foreground">{name}</span>
          <span className="block text-muted-foreground">{t(`promise.voices.${voice.id}.pet`)}</span>
        </span>
      </figcaption>
    </li>
  );
}

// Section C — made by people who get it. A signed maker's promise (real, honest
// warmth) carries the section; the early-owner voices are flagged placeholders
// (monogram avatars, no invented ratings) until genuine quotes replace them.
export function MakersPromise() {
  const { t } = useTranslation("landing");
  const { voices } = LANDING_CONTENT.promise;
  return (
    <Section id="promise" labelledBy="promise-heading" className="bg-paper">
      <Reveal>
        <div className="max-w-2xl">
          <SectionHeading id="promise-heading">{t("promise.heading")}</SectionHeading>
          <p className="mt-5 text-pretty text-lg leading-relaxed text-foreground/80">
            {t("promise.body")}
          </p>
          <p className="mt-5 font-display text-foreground">{t("promise.signature")}</p>
        </div>
      </Reveal>

      <div className="mt-14">
        <Reveal>
          <h3 className="font-display text-lg font-semibold tracking-tight text-foreground">
            {t("promise.voicesHeading")}
          </h3>
        </Reveal>
        <ul className="mt-6 grid gap-5 sm:grid-cols-3">
          {voices.map((voice, index) => (
            <VoiceCard key={voice.id} voice={voice} index={index} />
          ))}
        </ul>
        <PhotoBrief briefId="testimonial" className="mt-6" />
      </div>
    </Section>
  );
}
